import {
	FeedApi,
	FeedCorrection,
	FeedCorrectionInput,
	FeedReferencePoint,
	FeedTank,
	FeedReferencePointInData,
} from '@/api';
import { ApiUtils } from '@/common/ApiUtils';
import {
	AdditionalFeedParams,
	CustomFormulaCalculator,
	CustomFormulaResult,
	Millimeters,
} from '@/typings/custom-formula-calculator';

export class CorrectionService {
	private static feedApi: FeedApi = new FeedApi();

	public static async calculateFeedTankVolume(
		distances: Millimeters[],
		additionalFeedParams: AdditionalFeedParams,
		feedTank: FeedTank | null,
		parameters: Record<string, string | number>,
		correction: Record<string, number | null>,
		reading?: number,
	): Promise<{ result: CustomFormulaResult | Error | null; error?: string }> {
		if (!feedTank?.id || !feedTank?.formula) return { result: null };

		try {
			const result: CustomFormulaResult[] | Error = await CustomFormulaCalculator.calculateVolume({
				distances,
				entityType: 'feedTank',
				entityId: feedTank.id,
				entityParameters: parameters,
				entityCorrections: correction,
				formula: (<any>feedTank.formula)?.formula,
				formulaName: (<any>feedTank.formula)?.name,
				additionalFeedParams,
			});
			if (result instanceof Error) {
				return { result: null, error: result.message };
			}
			return {
				result: result[0],
			};
		} catch (ex) {
			const error = ApiUtils.handleApiError(ex, 'Unknown reason').errorMessage;
			console.error('Error calculating feed tank volume', ex);
			return { result: null, error: 'Error calculating feed tank volume: ' + error };
		}
	}

	public static async addCorrection(
		feedTankId: number,
		correction: FeedCorrectionInput,
	): Promise<{ error?: string; correction?: FeedCorrection }> {
		try {
			const c = await this.feedApi.addFeedTankCorrection(feedTankId, correction);
			return { correction: c?.data?.data };
		} catch (e) {
			let error = ApiUtils.handleApiError(e, 'Error adding correction').errorMessage;
			return { error };
		}
	}

	public static async deleteCorrection(
		feedTankId: number,
		correctionId: number,
	): Promise<{ error?: string }> {
		try {
			await this.feedApi.deleteFeedTankCorrection(feedTankId, correctionId);
			return {};
		} catch (e) {
			let error = ApiUtils.handleApiError(e, 'Error deleting correction').errorMessage;
			return { error };
		}
	}

	/**
	 * Store a feed tank reference point (e.g. refill) for strain tanks.
	 * Backend: storeReferencePoint(int $feedTankId, array $input): FeedReferencePoint
	 */
	public static async createFeedTankReferencePoint(
		feedTankId: number,
		payload: FeedReferencePointInData,
	): Promise<{ error?: string; referencePoint?: FeedReferencePoint }> {
		try {
			const res = await this.feedApi.createFeedTankReferencePoint(feedTankId, payload);
			return { referencePoint: res?.data };
		} catch (e) {
			const error = ApiUtils.handleApiError(e, 'Error adding feed tank reference point').errorMessage;
			return { error };
		}
	}

	/**
	 * Delete a feed tank reference point.
	 * Backend: destroyReferencePoint(int $feedTankId, int $referencePointId)
	 */
	public static async deleteFeedTankReferencePoint(
		feedTankId: number,
		referencePointId: number,
	): Promise<{ error?: string }> {
		try {
			await this.feedApi.deleteFeedTankReferencePoint(feedTankId, referencePointId);
			return {};
		} catch (e) {
			const error = ApiUtils.handleApiError(e, 'Error deleting feed tank reference point').errorMessage;
			return { error };
		}
	}
}


